/**
 * Headlight Bridge Inspection — navigation graph for pict-section-navigation.
 *
 * This is the micro app's whole menu. It replaces the platform configuration app's ~200-entry graph
 * (swapped in by pict-microapp via `setNavigationGraph`), so every destination a bridge inspector or
 * program manager needs has to be here — and nothing else should be.
 *
 * The node shape is the platform app's: `Hash` is stable identity, `Route` is an app hash route,
 * `Icon` is a Font Awesome class, `Accent` is a pict theme token. `Capability: '<Entity>-<Action>'`
 * gates an entry on the signed-in user's effective permissions; a `#/PSRS/<Entity>/List` route
 * derives `<Entity>-Read` automatically, so only non-PSRS entries need one spelled out. `Advanced`
 * entries stay hidden until the user asks to see advanced items or searches for them.
 *
 * Keep this in step with the route allow list in BridgeInspection-MicroApp-Manifest.js: a nav entry
 * pointing at a route the manifest does not expose is a dead link, and pict-microapp will not catch
 * it for you (it gates routes, not menu items).
 */

const BRIDGE_SECTIONS =
[
	{
		Name: 'Inspections', Hash: 'inspections', Icon: 'fas fa-clipboard-check', Accent: 'var(--theme-color-brand-primary)',
		Blurb: 'Plan the inspection program, then work the inspections themselves.',
		Children:
		[
			{
				Name: 'Inspection Schedule', Icon: 'fas fa-calendar-days', Route: '#/InspectionSchedule', Capability: 'Inspection-Read',
				Keywords: 'schedule calendar overview worklist upcoming overdue due deadline program oversight timeline year month week',
				Description: 'The program view of every inspection — what is due, what is overdue, and what each team is carrying.'
			},
			{
				Name: 'Inspections', Icon: 'fas fa-clipboard-list', Route: '#/PSRS/Inspection/List', Capability: 'Inspection-Read',
				Keywords: 'inspection list search routine nstm underwater element dossier status scheduled completed',
				Description: 'Every inspection as a searchable list — open one to work its documents, assets, inspectors and teams.'
			},
			{
				Name: 'Approval Throughput', Icon: 'fas fa-gauge-high', Route: '#/DocumentThroughput/Inspection-Dossier', Capability: 'Document-Read',
				Keywords: 'throughput approval workflow dossier cycle time bottleneck submitter reviewer pipeline turnaround',
				Description: 'How inspection dossiers move through approval — cycle times, where they stall, and who is carrying the load.'
			}
		]
	},
	{
		Name: 'Bridges', Hash: 'bridges', Icon: 'fas fa-bridge', Accent: 'var(--theme-color-status-success)',
		Blurb: 'The bridge inventory, on a map and in detail.',
		Children:
		[
			{
				Name: 'Bridge Dashboard', Icon: 'fas fa-map-location-dot', Route: '#/AssetDashboard', Capability: 'PhysicalAsset-Read',
				Keywords: 'bridge dashboard map browse overview inventory snbi asset structure county carried intersected search landing',
				Description: 'Every bridge on a map, with SNBI metadata and inspection status — the app’s landing screen.'
			},
			{
				Name: 'Bridge Inventory', Icon: 'fas fa-table-list', Route: '#/PSRS/PhysicalAsset/List', Capability: 'PhysicalAsset-Read',
				Keywords: 'asset inventory list bridge structure number search physical asset raw records table',
				Description: 'The bridge inventory as a searchable table — open one to get its full workspace.'
			}
		]
	},
	{
		Name: 'Teams', Hash: 'teams', Icon: 'fas fa-users', Accent: 'var(--theme-color-status-info)',
		Blurb: 'The crews doing the work, and what they have been assigned.',
		Children:
		[
			{
				Name: 'Teams', Icon: 'fas fa-users', Route: '#/PSRS/Team/List', Capability: 'Team-Read',
				Keywords: 'team crew inspector member roster assignment subteam lab project inspection who',
				Description: 'Inspection teams, their members, and the inspections and projects each one is assigned to.'
			}
		]
	},
	{
		Name: 'Field Data', Hash: 'field-data', Icon: 'fas fa-camera', Accent: 'var(--theme-color-status-warning)',
		Blurb: 'What came back from the field.',
		Children:
		[
			{
				Name: 'Observations', Icon: 'fas fa-clipboard-check', Route: '#/Observations', Capability: 'Observation-Read',
				Keywords: 'observation field data photo artifact capture list chart map explore faceted search defect condition',
				Description: 'Field observations as a list, a set of charts, or a map — cross-filter by type and reporting status.'
			},
			{
				Name: 'Documents', Icon: 'fas fa-file-lines', Route: '#/Documents/Browse', Capability: 'Document-Read',
				Keywords: 'document browse report form dossier element inspection approval status search list chart',
				Description: 'Browse the documents behind the inspections — reports, forms and dossiers, with their approval state.'
			}
		]
	},
	{
		Name: 'Asset Setup', Hash: 'asset-setup', Icon: 'fas fa-gears', Accent: 'var(--theme-color-text-muted)',
		Blurb: 'Configure what a bridge record carries and where the catalog comes from.',
		Children:
		[
			{
				Name: 'Metadata Shape', Icon: 'fas fa-sliders', Route: '#/AssetTypeDescriptor', Capability: 'AssetType-Read', Advanced: true,
				Keywords: 'metadata shape descriptor show hide visible field label units help metric map display customize rename reorder publish draft snbi',
				Description: 'What a bridge\u2019s detail panel shows: which values appear, what they are called, their units and help, and which the map can be coloured by.'
			},
			{
				Name: 'Asset Type Manifests', Icon: 'fas fa-diagram-project', Route: '#/AssetTypeManifests', Capability: 'AssetType-Read', Advanced: true,
				Keywords: 'asset type manifest custom property schema snbi submission field definition configure metadata',
				Description: 'The custom-property schema each asset type carries — including the SNBI submission shape.'
			},
			{
				Name: 'Sync Asset Catalog', Icon: 'fas fa-cloud-arrow-down', Route: '#/AssetCatalogSync', Capability: 'PhysicalAsset-Create', Advanced: true,
				Keywords: 'sync catalog aashto seed element type defect material public data lake import bootstrap',
				Description: 'Seed this customer’s asset element, defect and material catalog from the shared public data lake.'
			}
		]
	}
];

module.exports = BRIDGE_SECTIONS;
module.exports.BRIDGE_SECTIONS = BRIDGE_SECTIONS;
